import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

interface DialogOptions {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: 'default' | 'danger';
}

interface DialogViewProps extends DialogOptions {
    message: string;
    confirmation: boolean;
    settle: (result: boolean) => void;
}

const DialogView: React.FC<DialogViewProps> = ({ message, confirmation, title, confirmLabel, cancelLabel, tone = 'default', settle }) => {
    const primaryButton = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        primaryButton.current?.focus();
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape')
                settle(false);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [settle]);

    return <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" role="presentation">
        <section className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" role={confirmation ? 'dialog' : 'alertdialog'} aria-modal="true" aria-labelledby="app-dialog-title" aria-describedby="app-dialog-message">
            <div className="p-6">
                <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-full ${tone === 'danger' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={tone === 'danger' ? 'M12 9v3m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z' : 'M12 9h.01M11 12h1v4h1m8-4a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'} /></svg>
                </div>
                <h2 id="app-dialog-title" className="text-xl font-extrabold text-slate-900">{title || (confirmation ? 'Please Confirm' : 'IntelOptics Insights')}</h2>
                <p id="app-dialog-message" className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">{message}</p>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
                {confirmation && <button type="button" onClick={() => settle(false)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100">{cancelLabel || 'Cancel'}</button>}
                <button ref={primaryButton} type="button" onClick={() => settle(true)} className={`rounded-lg px-4 py-2 text-sm font-bold text-white ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>{confirmLabel || (confirmation ? 'Confirm' : 'OK')}</button>
            </div>
        </section>
    </div>;
};

const openAppDialog = (message: string, confirmation: boolean, options: DialogOptions = {}) => new Promise<boolean>(resolve => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;
    const settle = (result: boolean) => {
        if (settled)
            return;
        settled = true;
        resolve(result);
        queueMicrotask(() => {
            root.unmount();
            container.remove();
        });
    };
    root.render(<DialogView {...options} message={String(message)} confirmation={confirmation} settle={settle} />);
});

export const confirmApp = (message: string, options?: DialogOptions) => openAppDialog(message, true, options);
export const showAppAlert = async (message: string, options?: DialogOptions) => { await openAppDialog(message, false, options); };
